import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Paper,
  Tab,
  Tabs,
  IconButton,
  InputBase,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  ImageOutlined as ImageIcon,
  FolderOutlined as FolderIcon,
  InsertDriveFileOutlined as FileIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import FolderBrowser from './FolderBrowser';
import FileUploader from './FileUploader';
import FileDetails from './FileDetails';
import type { MediaFile, MediaImage } from '../../models/media';

interface MediaDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect?: (file: MediaFile | MediaImage) => void;
  fileTypeFilter?: 'all' | 'images' | 'documents';
}

const MediaDialog: React.FC<MediaDialogProps> = ({
  open,
  onClose,
  onSelect,
  fileTypeFilter = 'all'
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'images' | 'documents'>(fileTypeFilter);
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [currentFolder, setCurrentFolder] = useState<number | null | undefined>(undefined);
  const [fileDetailsOpen, setFileDetailsOpen] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<MediaFile | MediaImage | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const handleTabChange = (_: React.SyntheticEvent, newValue: 'all' | 'images' | 'documents') => {
    setActiveTab(newValue);
  };
  
  const handleFileSelect = (file: MediaFile | MediaImage) => {
    setSelectedFile(file);
    
    if (onSelect) {
      onSelect(file);
    } else {
      setFileDetailsOpen(true);
    }
  };
  
  const handleFileUpdate = (file: MediaFile | MediaImage) => {
    setSelectedFile(file);
  };
  
  const handleFolderChange = (folderId: number | null | undefined) => {
    setCurrentFolder(folderId);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="media-dialog-title"
    >
      <DialogTitle id="media-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Media Library
        <IconButton edge="end" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2
          }}>
            {/* Search */}
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1,
              flex: { xs: '1', sm: '0 1 auto' },
              width: { xs: '100%', sm: '300px' }
            }}>
              <InputBase
                placeholder={`Search ${currentFolder !== undefined ? 'in current folder' : 'all media'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ ml: 1, flex: 1 }}
              />
              <IconButton size="small">
                <SearchIcon />
              </IconButton>
            </Box>
            
            {/* Upload Button */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload
              </Button>
            </Box>
          </Box>
          
          <Divider />
          
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="media type tabs"
            variant="fullWidth"
          >
            <Tab 
              label="All Files" 
              value="all" 
              icon={<FolderIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Images" 
              value="images" 
              icon={<ImageIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Documents" 
              value="documents" 
              icon={<FileIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Paper>
        
        <Paper sx={{ p: 3, minHeight: '400px', maxHeight: 'calc(80vh - 200px)', overflowY: 'auto' }}>
          <FolderBrowser
            onSelectFile={handleFileSelect}
            onUploadClick={() => setUploadDialogOpen(true)}
            fileTypeFilter={activeTab}
            currentFolderId={currentFolder}
            onFolderChange={handleFolderChange}
            selectionMode={!!onSelect}
            searchQuery={searchQuery}
          />
        </Paper>
      </DialogContent>
      
      {!onSelect && (
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
      
      {/* Upload Dialog */}
      <FileUploader
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        currentFolderId={currentFolder}
        onUploadComplete={() => {
          setUploadDialogOpen(false);
          
          // Force refresh by briefly setting to undefined and back to current value
          const currentValue = currentFolder;
          setCurrentFolder(undefined);
          setTimeout(() => {
            setCurrentFolder(currentValue);
          }, 50);
        }}
      />
      
      {/* File Details Dialog */}
      {selectedFile && !onSelect && (
        <FileDetails
          file={selectedFile}
          open={fileDetailsOpen}
          onClose={() => setFileDetailsOpen(false)}
          onUpdate={handleFileUpdate}
        />
      )}
    </Dialog>
  );
};

export default MediaDialog;
